import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MasterService } from 'app/service/master.service';
import {
  ASSIGNER_BULK_MAX_ROWS,
  AssignerBulkEntityType,
  AssignerBulkLookupMaps,
  AssignerBulkLookupOption,
  AssignerBulkParseResult,
  AssignerBulkPreviewColumn,
  AssignerBulkPreviewRow,
  AssignerBulkTemplateFormat,
  PRODUCT_BULK_COLUMNS,
  RECIPE_BULK_COLUMNS,
} from './assigner-bulk-upload.models';
import {
  downloadAssignerBulkTemplate,
  downloadCsvTemplate,
  extractMetadataOptions,
  parseExcelArrayBuffer,
  rebuildProductPayload,
  rebuildRecipePayload,
  refreshParseCounts,
  validateAssignerBulkCsv,
  validateAssignerBulkRows,
} from './assigner-bulk-upload.util';

@Component({
  selector: 'app-assigner-bulk-upload-modal',
  templateUrl: './assigner-bulk-upload-modal.component.html',
  styleUrls: ['./assigner-bulk-upload-modal.component.scss'],
})
export class AssignerBulkUploadModalComponent implements OnInit, AfterViewInit {
  @Input() entityType: AssignerBulkEntityType = 'recipe';

  readonly maxRows = ASSIGNER_BULK_MAX_ROWS;
  step: 'upload' | 'preview' = 'upload';
  loadingLookups = false;
  parsing = false;
  fileName = '';
  fileError = '';
  parseResult: AssignerBulkParseResult | null = null;

  sourceOptions: AssignerBulkLookupOption[] = [];
  dataSourceOptions: AssignerBulkLookupOption[] = [];
  databaseSourceOptions: AssignerBulkLookupOption[] = [];
  countryOptions: AssignerBulkLookupOption[] = [];

  private lookups: AssignerBulkLookupMaps = {};

  constructor(
    public activeModal: NgbActiveModal,
    private masterService: MasterService
  ) {}

  get title(): string {
    if (this.entityType === 'recipe') return 'Bulk Upload Recipes';
    if (this.entityType === 'finishProduct') return 'Bulk Upload Finish Products';
    return 'Bulk Upload Supplements';
  }

  get subtitle(): string {
    return this.step === 'upload'
      ? 'Download the template, fill Assigner create fields, then upload the file (.xlsx / .csv).'
      : 'Review rows and fill dropdowns if needed. Only valid rows will be imported as To be assigned.';
  }

  get columns(): AssignerBulkPreviewColumn[] {
    return this.entityType === 'recipe' ? RECIPE_BULK_COLUMNS : PRODUCT_BULK_COLUMNS;
  }

  get validCount(): number {
    return this.parseResult?.validCount || 0;
  }

  get errorCount(): number {
    return this.parseResult?.errorCount || 0;
  }

  get totalCount(): number {
    return this.parseResult?.totalCount || 0;
  }

  get isRecipe(): boolean {
    return this.entityType === 'recipe';
  }

  async ngOnInit(): Promise<void> {
    await this.loadLookups();
    this.scheduleLucideIconRender();
  }

  ngAfterViewInit(): void {
    this.scheduleLucideIconRender();
  }

  downloadTemplate(format: AssignerBulkTemplateFormat = 'xlsx'): void {
    downloadAssignerBulkTemplate(this.entityType, format);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const isExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');
    const isCsv = lowerName.endsWith('.csv');

    if (!isExcel && !isCsv) {
      this.fileError = 'Please upload a valid Excel (.xlsx / .xls) or CSV file.';
      input.value = '';
      return;
    }

    this.fileError = '';
    this.fileName = file.name;
    this.parsing = true;

    const reader = new FileReader();

    if (isExcel) {
      reader.onload = () => {
        try {
          const buffer = reader.result as ArrayBuffer;
          const { headers, rows } = parseExcelArrayBuffer(buffer);
          this.parseResult = validateAssignerBulkRows(this.entityType, headers, rows, this.lookups);
          if (this.parseResult.fileError) {
            this.fileError = this.parseResult.fileError;
            this.step = 'upload';
          } else if (!this.parseResult.totalCount) {
            this.fileError = 'No data rows found in the Excel sheet.';
            this.step = 'upload';
          } else {
            this.step = 'preview';
          }
        } catch (err) {
          console.error('Failed to parse assigner bulk Excel file', err);
          this.fileError = 'Failed to parse Excel file.';
          this.step = 'upload';
        } finally {
          this.parsing = false;
          this.scheduleLucideIconRender();
        }
      };
      reader.onerror = () => {
        this.parsing = false;
        this.fileError = 'Failed to read Excel file.';
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = () => {
        try {
          const text = String(reader.result || '');
          this.parseResult = validateAssignerBulkCsv(this.entityType, text, this.lookups);
          if (this.parseResult.fileError) {
            this.fileError = this.parseResult.fileError;
            this.step = 'upload';
          } else if (!this.parseResult.totalCount) {
            this.fileError = 'No data rows found in the CSV.';
            this.step = 'upload';
          } else {
            this.step = 'preview';
          }
        } catch (err) {
          console.error('Failed to parse assigner bulk CSV', err);
          this.fileError = 'Failed to parse CSV file.';
          this.step = 'upload';
        } finally {
          this.parsing = false;
          this.scheduleLucideIconRender();
        }
      };
      reader.onerror = () => {
        this.parsing = false;
        this.fileError = 'Failed to read CSV file.';
      };
      reader.readAsText(file);
    }

    input.value = '';
  }

  backToUpload(): void {
    this.step = 'upload';
    this.parseResult = null;
    this.fileName = '';
    this.fileError = '';
    this.scheduleLucideIconRender();
  }

  onSourceChange(row: AssignerBulkPreviewRow): void {
    rebuildRecipePayload(row, this.lookups);
    this.refreshCountsAndDuplicates();
  }

  onProductLookupChange(row: AssignerBulkPreviewRow): void {
    rebuildProductPayload(row, this.lookups, this.entityType);
    this.refreshCountsAndDuplicates();
  }

  deleteRow(index: number): void {
    if (!this.parseResult?.rows?.length) return;
    if (index < 0 || index >= this.parseResult.rows.length) return;

    this.parseResult.rows.splice(index, 1);
    this.parseResult.rows.forEach((row, i) => {
      row.rowNumber = i + 1;
    });

    if (!this.parseResult.rows.length) {
      this.backToUpload();
      return;
    }

    this.refreshCountsAndDuplicates();
    this.scheduleLucideIconRender();
  }

  confirmImport(): void {
    const validPayloads = (this.parseResult?.rows || [])
      .filter((r) => r.valid && r.payload)
      .map((r) => r.payload as Record<string, unknown>);
    if (!validPayloads.length) return;
    this.activeModal.close(validPayloads);
  }

  private refreshCountsAndDuplicates(): void {
    if (!this.parseResult) return;

    if (this.isRecipe) {
      const seen = new Map<string, number>();
      this.parseResult.rows.forEach((row) => {
        const key = (row.display.RecipeName || '').trim().toLowerCase();
        if (!key) return;
        const count = (seen.get(key) || 0) + 1;
        seen.set(key, count);
      });
      this.parseResult.rows.forEach((row) => {
        rebuildRecipePayload(row, this.lookups);
        const key = (row.display.RecipeName || '').trim().toLowerCase();
        if (key && (seen.get(key) || 0) > 1) {
          row.valid = false;
          row.payload = undefined;
          const dupMsg = `Duplicate RecipeName "${row.display.RecipeName}" in this CSV`;
          row.error = row.error ? `${row.error}; ${dupMsg}` : dupMsg;
        }
      });
    } else {
      const seen = new Map<string, number>();
      this.parseResult.rows.forEach((row) => {
        const key = `${(row.display.BrandName || '').trim().toLowerCase()}||${(row.display.ProductName || '').trim().toLowerCase()}`;
        if (key === '||') return;
        seen.set(key, (seen.get(key) || 0) + 1);
      });
      this.parseResult.rows.forEach((row) => {
        rebuildProductPayload(row, this.lookups, this.entityType);
        const key = `${(row.display.BrandName || '').trim().toLowerCase()}||${(row.display.ProductName || '').trim().toLowerCase()}`;
        if (key !== '||' && (seen.get(key) || 0) > 1) {
          row.valid = false;
          row.payload = undefined;
          const dupMsg = `Duplicate BrandName+ProductName "${row.display.BrandName} / ${row.display.ProductName}" in this CSV`;
          row.error = row.error ? `${row.error}; ${dupMsg}` : dupMsg;
        }
      });
    }

    refreshParseCounts(this.parseResult);
  }

  private async loadLookups(): Promise<void> {
    this.loadingLookups = true;
    try {
      if (this.entityType === 'recipe') {
        const meta: any = await this.masterService.getRecipeMetadata('recipeLinkSources');
        const root = meta?.response ?? meta?.data ?? meta ?? {};
        this.sourceOptions = extractMetadataOptions(
          root.recipeLinkSources || root.RecipeLinkSources
        );
        this.lookups = { sources: this.sourceOptions };
      } else {
        const [productMeta, countriesMeta]: any[] = await Promise.all([
          this.masterService.getProductMetadata('ingredientItemTypes,recipeLinkSources'),
          this.masterService.getProductMetadata('countries'),
        ]);
        const productRoot = productMeta?.response ?? productMeta?.data ?? productMeta ?? {};
        const countryRoot = countriesMeta?.response ?? countriesMeta?.data ?? countriesMeta ?? {};
        const countries =
          countryRoot.countries ||
          countryRoot.Countries ||
          countryRoot.items ||
          countryRoot;
        this.dataSourceOptions = extractMetadataOptions(
          productRoot.ingredientItemTypes || productRoot.IngredientItemTypes
        );
        this.databaseSourceOptions = extractMetadataOptions(
          productRoot.recipeLinkSources || productRoot.RecipeLinkSources
        );
        this.countryOptions = extractMetadataOptions(
          Array.isArray(countries) ? countries : []
        );
        this.lookups = {
          dataSources: this.dataSourceOptions,
          databaseSources: this.databaseSourceOptions,
          countries: this.countryOptions,
        };
      }
    } catch (err) {
      console.error('Failed to load assigner bulk upload lookups', err);
    } finally {
      this.loadingLookups = false;
    }
  }

  private scheduleLucideIconRender(): void {
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      if ((window as any).lucide?.createIcons) {
        (window as any).lucide.createIcons();
      }
    }, 50);
  }
}
